import React, { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import ListRoundedIcon from "@mui/icons-material/ListRounded";
import { brand } from "../../theme/businessFlowsTheme";
import { menuPaper } from "../workspaces/WorkspaceUi";

// Signed-in pages scroll inside .main-content-area, not the window, so the rail
// follows that element when it exists and the window otherwise.
const scrollerOf = (node) => (node && node.closest && node.closest(".main-content-area")) || null;

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)").matches : false;

// The contents of the guide, and where the reader is in it. Reading marks the
// section the reader has reached; clicking or pressing Enter takes them there
// and moves focus with them, so the keyboard doesn't get left behind.
export default function ContentsRail({ sections }) {
  const [active, setActive] = useState(sections[0]?.id || "");
  const [menu, setMenu] = useState(null);
  const anchor = useRef(null);

  useEffect(() => {
    const scroller = scrollerOf(anchor.current) || window;
    const top = () => (scroller === window ? 0 : scroller.getBoundingClientRect().top);
    let frame = 0;
    const measure = () => {
      frame = 0;
      const edge = top() + 140;
      let current = sections[0]?.id || "";
      sections.forEach(({ id }) => {
        const node = document.getElementById(id);
        if (node && node.getBoundingClientRect().top <= edge) current = id;
      });
      setActive((was) => (was === current ? was : current));
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };
    measure();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      scroller.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [sections]);

  const go = useCallback((event, id) => {
    if (event) event.preventDefault();
    setMenu(null);
    const node = document.getElementById(id);
    if (!node) return;
    setActive(id);
    node.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block: "start" });
    if (typeof node.focus === "function") node.focus({ preventScroll: true });
  }, []);

  const current = sections.find((section) => section.id === active) || sections[0];

  const link = (section) => ({
    href: `#${section.id}`,
    onClick: (event) => go(event, section.id),
    "aria-current": section.id === active ? "true" : undefined,
  });

  return (
    <Box
      ref={anchor}
      component="nav"
      aria-label="On this page"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 2,
        alignSelf: "start",
        mb: { xs: 2.5, md: 0 },
        // On a phone the rail is one line that opens the same list as a menu,
        // and keeps a hairline under it so the guide scrolls behind it clearly.
        bgcolor: { xs: brand.canvas, md: "transparent" },
        borderBottom: { xs: `1px solid ${brand.line}`, md: "none" },
        py: { xs: 1, md: 1.5 },
      }}
    >
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <Typography component="h2" sx={{ fontSize: "0.79rem", fontWeight: 650, color: brand.muted, mb: 1.5, pl: 1.75 }}>
          On this page
        </Typography>
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
          {sections.map((section) => (
            <Box component="li" key={section.id}>
              <Box
                component="a"
                {...link(section)}
                sx={{
                  display: "block",
                  py: 0.8,
                  pl: 1.75,
                  pr: 1,
                  fontSize: "0.89rem",
                  lineHeight: 1.45,
                  textDecoration: "none",
                  borderLeft: "3px solid",
                  borderColor: section.id === active ? brand.signal : brand.line,
                  color: section.id === active ? brand.ink : brand.muted,
                  fontWeight: section.id === active ? 650 : 500,
                  transition: "color 120ms, border-color 120ms",
                  "&:hover": { color: brand.ink, textDecoration: "none" },
                  "&:focus-visible": { outline: `2px solid ${brand.ink}`, outlineOffset: 2, borderRadius: "4px" },
                }}
              >
                {section.label}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <Button
          onClick={(event) => setMenu(event.currentTarget)}
          startIcon={<ListRoundedIcon />}
          aria-haspopup="menu"
          aria-expanded={menu ? "true" : undefined}
          sx={{
            width: "100%",
            justifyContent: "flex-start",
            bgcolor: "#fff",
            border: `1px solid ${brand.line}`,
            color: brand.ink,
            fontWeight: 600,
            py: 1,
            "&:hover": { bgcolor: "#fff", borderColor: "#c9ccd4" },
            "&.Mui-focusVisible": { outline: `2px solid ${brand.ink}`, outlineOffset: 2 },
          }}
        >
          On this page: {current?.label}
        </Button>
        <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)} {...menuPaper}>
          {sections.map((section) => (
            <MenuItem key={section.id} selected={section.id === active} onClick={(event) => go(event, section.id)} sx={{ fontSize: "0.92rem" }}>
              {section.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Box>
  );
}
